(() => {
  if (customElements.get('fit-finder')) return;

  const poundsPerKg = 2.20462262185;
  const pretty = (number) => String(Number(number.toFixed(3)));
  const rounded = (number) => String(Number(number.toFixed(1)));
  const normalizeSize = (value) => {
    const size = String(value || '').trim().toUpperCase();
    return ({ XXL: '2XL', XXXL: '3XL', XXXXL: '4XL' })[size] || size;
  };

  class FitFinder extends HTMLElement {
    // Ease is a store fit preference, not a learned confidence score.
    static matchChest(chest, preference, model, garments, outsideData = false) {
      if (!Number.isFinite(chest) || !garments.length) return null;
      const target = chest + model.sizing.ease[preference];
      const chart = garments.map((g) => g.width * 2);
      const index = chart.findIndex((circumference) => circumference >= target);
      if (index < 0) return { sizes: [garments.at(-1).size], exceedsRange: true, target };
      if (target < chart[0]) return { sizes: [garments[0].size], runsLarge: true, target };
      const edge = chart.slice(0, -1).findIndex((circumference) => Math.abs(target - circumference) <= model.sizing.boundaryBand + 1e-9);
      let indices = edge >= 0 ? [edge, edge + 1] : [index];
      if (outsideData && indices.length === 1 && garments.length > 1) {
        // Show the two neighboring chart sizes around an extrapolated target.
        indices = index > 0 ? [index - 1, index] : [0, 1];
      }
      return { sizes: indices.map((i) => garments[i].size), target };
    }

    static estimate({ weight, height, preference }, model, garments) {
      if (!garments.length || !Number.isFinite(weight) || weight < 80 || weight > 400 ||
          !Number.isFinite(height) || height < 48 || height > 84 ||
          !['standard', 'oversized'].includes(preference)) return null;
      // One provisional estimate for the store's predominantly male customer base.
      const group = model.groups.male;
      const support = group.support;
      const h = height - support.mean[0];
      const w = weight - support.mean[1];
      const inv = support.inverseCovariance;
      const distance = h * h * inv[0][0] + h * w * (inv[0][1] + inv[1][0]) + w * w * inv[1][1];
      const outsideData = height < support.height[0] || height > support.height[1] ||
        weight < support.weight[0] || weight > support.weight[1] || distance > support.maxDistanceSquared;
      const estimatedBodyChest = group.intercept + group.height * height + group.weight * weight;
      return { ...this.matchChest(estimatedBodyChest, preference, model, garments, outsideData), estimatedBodyChest, outsideData };
    }

    static compare(garments, width, length) {
      if (!garments.length || ![width, length].every((value) => Number.isFinite(value) && value > 0)) return null;
      const nearest = [...garments].sort((a, b) => Math.abs(a.width - width) - Math.abs(b.width - width)).slice(0, 2);
      return {
        sizes: nearest.map((garment) => garment.size),
        outside: width < Math.min(...garments.map((g) => g.width)) || width > Math.max(...garments.map((g) => g.width)) ||
          length < Math.min(...garments.map((g) => g.length)) || length > Math.max(...garments.map((g) => g.length)),
      };
    }

    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.root = this.closest('product-info');
      this.body = this.querySelector('.fit-finder__body');
      this.form = this.querySelector('[data-fit-estimate-form]');
      this.measureForm = this.querySelector('[data-fit-measure-form]');
      this.result = this.querySelector('[data-fit-result]');
      this.error = this.querySelector('[data-fit-error]');
      this.measureError = this.querySelector('[data-fit-measure-error]');
      this.comparison = this.querySelector('[data-fit-comparison]');
      this.model = JSON.parse(this.querySelector('[data-fit-model]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-fit-variants]').textContent);
      // The rendered chart is the single source for garment dimensions.
      this.garments = Array.from(this.querySelectorAll('[data-fit-row]'), (row) => ({
        size: row.dataset.fitRow, width: Number(row.dataset.width), length: Number(row.dataset.length),
      }));
      this.unit = 'in';
      this.bodyUnit = 'us';
      this.values = {};
      this.candidates = [];
      this.selectedSize = null;
      this.measured = false;
      this.tab = 'suggest';
      this.querySelectorAll('[data-fit-chart-size]').forEach((button) => button.setAttribute('aria-pressed', 'false'));

      this.renderRows();
      this.root?.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-fit-finder-open]');
        if (opener?.dataset.fitFinderOpen !== this.dialog.id) return;
        this.opener = opener;
        this.renderSelection();
        this.renderResultChoices();
        this.dialog.showModal();
      }, options);
      this.dialog.addEventListener('close', () => this.opener?.focus({ preventScroll: true }), options);
      this.dialog.addEventListener('click', (event) => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target.closest('[data-fit-close]') || (event.target === this.dialog &&
          (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom))) {
          this.dialog.close();
          return;
        }
        if (event.target.closest('[data-fit-edit]')) {
          this.setResultView(false);
          this.form.querySelector(this.bodyUnit === 'us' ? '[data-fit-feet]' : '[data-fit-height-cm]').focus();
        }
        const tab = event.target.closest('[data-fit-tab]');
        if (tab) {
          // Rebuild the guide's candidates if a separate tee comparison changed the chart.
          if (tab.closest('[data-fit-result]')) this.suggest();
          this.showTab(tab.dataset.fitTab);
        }
        const unit = event.target.closest('[data-fit-unit]');
        if (unit) this.setUnit(unit.dataset.fitUnit);
        const bodyUnit = event.target.closest('[data-fit-body-unit]');
        if (bodyUnit) this.setBodyUnit(bodyUnit.dataset.fitBodyUnit);
        const chartSize = event.target.closest('[data-fit-chart-size]');
        if (chartSize) {
          this.selectedSize = chartSize.value;
          this.querySelectorAll('[data-fit-chart-size]').forEach((button) => button.setAttribute('aria-pressed', String(button === chartSize)));
          this.renderSelection();
          this.querySelector('[data-fit-selection]').focus();
        }
        const resultChoice = event.target.closest('[data-fit-result-select]');
        if (resultChoice && !resultChoice.disabled) {
          this.selectedSize = resultChoice.value;
          this.renderResultChoices();
          this.applySize();
        }
        if (event.target.closest('[data-fit-apply]')) this.applySize();
      }, options);
      this.form.addEventListener('input', (event) => {
        if (event.target.matches('[data-fit-weight]')) {
          this.values.weight = event.target.value === '' ? NaN : Number(event.target.value) * (this.bodyUnit === 'us' ? 1 : poundsPerKg);
          event.target.removeAttribute('aria-invalid');
        }
        if (event.target.matches('[data-fit-feet], [data-fit-height-inches], [data-fit-height-cm]')) {
          this.values.height = this.readHeight();
          event.target.removeAttribute('aria-invalid');
        }
        this.result.hidden = true;
        this.error.hidden = true;
        if (!this.measured) this.clearSelection();
      }, options);
      this.measureForm.addEventListener('input', (event) => {
        const key = event.target.matches('[data-fit-width]') ? 'width' : 'length';
        this.values[key] = event.target.value === '' ? NaN : Number(event.target.value) / (this.unit === 'cm' ? 2.54 : 1);
        event.target.removeAttribute('aria-invalid');
        this.measureError.hidden = true;
        this.comparison.hidden = true;
        this.measured = false;
        this.clearSelection();
      }, options);
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.suggest(); }, options);
      this.measureForm.addEventListener('submit', (event) => { event.preventDefault(); this.compare(); }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
    }

    clearSelection() {
      this.candidates = [];
      this.selectedSize = null;
      this.querySelectorAll('[data-fit-chart-size]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      this.renderRows();
      this.renderSelection();
    }

    showTab(tab, focus = true) {
      this.tab = tab;
      this.querySelectorAll('[data-fit-panel]').forEach((panel) => { panel.hidden = panel.dataset.fitPanel !== tab; });
      this.querySelectorAll('.fit-finder__tabs [data-fit-tab]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.fitTab === tab));
      });
      this.dialog.setAttribute('aria-describedby', this.querySelector(`[data-fit-panel="${tab}"] [data-fit-intro]`).id);
      this.body.scrollTop = 0;
      if (focus) this.querySelector(`.fit-finder__tabs [data-fit-tab="${tab}"]`).focus({ preventScroll: true });
    }

    display(inches) { return this.unit === 'cm' ? rounded(inches * 2.54) : pretty(inches); }

    setUnit(next) {
      if (next === this.unit) return;
      this.unit = next;
      this.querySelectorAll('[data-fit-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.fitUnit === next)));
      this.querySelector('[data-fit-table-unit]').textContent = next === 'cm' ? 'centimeters' : 'inches';
      this.querySelectorAll('[data-fit-measure-unit]').forEach((label) => { label.textContent = `(${next})`; });
      this.querySelectorAll('[data-fit-inches]').forEach((cell) => { cell.textContent = this.display(Number(cell.dataset.fitInches)); });
      for (const key of ['width', 'length']) {
        const input = this.querySelector(`[data-fit-${key}]`);
        input.value = Number.isFinite(this.values[key]) ? this.display(this.values[key]) : '';
        input.placeholder = this.display(key === 'width' ? 20.25 : 28);
      }
      this.renderSelection();
    }

    readHeight() {
      if (this.bodyUnit === 'metric') {
        const cm = this.querySelector('[data-fit-height-cm]');
        return cm.value === '' ? NaN : Number(cm.value) / 2.54;
      }
      const feet = this.querySelector('[data-fit-feet]');
      const inches = this.querySelector('[data-fit-height-inches]');
      if (!feet.validity.valid || !inches.validity.valid || feet.value === '' || inches.value === '') return NaN;
      return Number(feet.value) * 12 + Number(inches.value);
    }

    setBodyUnit(next) {
      if (next === this.bodyUnit) return;
      this.bodyUnit = next;
      const metric = next === 'metric';
      const input = this.querySelector('[data-fit-weight]');
      // Exact canonical values change only on user input, never on unit toggles.
      input.value = Number.isFinite(this.values.weight) ? rounded(this.values.weight / (metric ? poundsPerKg : 1)) : '';
      input.placeholder = metric ? '70' : '155';
      input.min = metric ? '36.2' : '80';
      input.max = metric ? '181.5' : '400';
      this.querySelector('[data-fit-weight-unit]').textContent = metric ? '(kg)' : '(lb)';
      this.querySelectorAll('[data-fit-body-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.fitBodyUnit === next)));
      const feet = this.querySelector('[data-fit-feet]');
      const inches = this.querySelector('[data-fit-height-inches]');
      const cm = this.querySelector('[data-fit-height-cm]');
      const height = this.values.height;
      this.querySelector('[data-fit-imperial]').hidden = metric;
      this.querySelector('[data-fit-metric]').hidden = !metric;
      feet.disabled = inches.disabled = metric;
      cm.disabled = !metric;
      feet.value = Number.isFinite(height) ? String(Math.floor(Math.round(height) / 12)) : '';
      inches.value = Number.isFinite(height) ? String(Math.round(height) % 12) : '';
      cm.value = Number.isFinite(height) ? rounded(height * 2.54) : '';
    }

    fail(error, message, input) {
      error.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    }

    suggest() {
      this.result.hidden = true;
      this.error.hidden = true;
      const input = this.querySelector('[data-fit-weight]');
      const heightInput = this.querySelector(this.bodyUnit === 'us' ? '[data-fit-feet]' : '[data-fit-height-cm]');
      if (!Number.isFinite(this.values.height) || this.values.height < 48 || this.values.height > 84) {
        this.fail(this.error, this.bodyUnit === 'us' ? 'Enter a height from 4 ft to 7 ft, including inches (use 0 for whole feet).' : 'Enter a height from 121.92 to 213.36 cm.', heightInput);
        return;
      }
      if (input.validity.badInput || input.value === '' || !Number.isFinite(this.values.weight) || this.values.weight < 80 || this.values.weight > 400) {
        this.fail(this.error, this.bodyUnit === 'us' ? 'Enter a weight from 80 to 400 lb.' : 'Enter a weight from 36.29 to 181.43 kg.', input);
        return;
      }
      const preference = this.form.querySelector('[name^="fit-preference-"]:checked').value;
      const estimate = this.constructor.estimate({ weight: this.values.weight, height: this.values.height,
        preference }, this.model, this.garments);
      if (!estimate) {
        this.error.textContent = 'This product has no sizes supported by this guide.';
        this.error.hidden = false;
        return;
      }
      this.measured = false;
      this.comparison.hidden = true;
      this.clearSelection();
      this.candidates = estimate.sizes;
      this.resultSizes = estimate.sizes;
      this.resultPreference = preference;
      const pair = estimate.sizes.length === 2;
      this.querySelector('[data-fit-result-label]').textContent = estimate.exceedsRange || estimate.runsLarge
        ? 'Closest size we offer' : pair ? 'Two sizes to compare' : 'Your size suggestion';
      this.querySelector('[data-fit-result-size]').textContent = estimate.sizes.join(' or ');
      this.querySelector('[data-fit-result-copy]').textContent = estimate.exceedsRange
        ? `${this.garments.at(-1).size} is the largest size offered on this product and may still be too snug. Check its measurements before choosing.`
        : estimate.runsLarge ? `${this.garments[0].size} is the smallest size offered on this product and may feel extra roomy. Check its measurements before choosing.`
        : pair ? 'Choose how you’d like it to feel. Both are starting points for your fit.'
        : 'A starting point for your fit.';
      this.result.querySelector('[data-fit-tab]').textContent = 'Compare measurements first';
      this.renderResultChoices();
      this.renderRows();
      this.setResultView(true);
      this.result.focus();
    }

    setResultView(show) {
      this.form.hidden = show;
      this.querySelector('[data-fit-panel="suggest"] [data-fit-intro]').hidden = show;
      this.result.hidden = !show;
      this.body.scrollTop = 0;
    }

    resetFinder() {
      this.form.reset();
      this.measureForm.reset();
      this.values = {};
      this.resultSizes = [];
      this.resultPreference = 'standard';
      this.measured = false;
      this.setBodyUnit('us');
      this.setUnit('in');
      this.querySelectorAll('[aria-invalid]').forEach((input) => input.removeAttribute('aria-invalid'));
      this.querySelectorAll('details').forEach((details) => { details.open = false; });
      this.error.hidden = this.measureError.hidden = this.comparison.hidden = true;
      this.setResultView(false);
      this.clearSelection();
      this.renderResultChoices();
      this.showTab('suggest', false);
    }

    renderResultChoices() {
      const sizes = this.resultSizes || [];
      this.querySelectorAll('[data-fit-result-option]').forEach((option, index) => {
        const size = sizes[index];
        option.hidden = !size;
        if (!size) return;
        const button = option.querySelector('[data-fit-result-select]');
        const stock = this.stockMessage(size);
        button.value = size;
        button.disabled = Boolean(stock);
        option.querySelector('[data-fit-choice-size]').textContent = size;
        option.querySelector('[data-fit-choice-feel]').textContent = sizes.length === 2
          ? index === 0 ? 'Closer fit' : 'More room'
          : this.resultPreference === 'oversized' ? 'For your oversized fit' : 'For your relaxed fit';
        option.querySelector('.fit-finder__choice-action').textContent = `Select ${size}`;
        option.querySelector('[data-fit-choice-stock]').textContent = stock;
      });
    }

    compare() {
      this.measureError.hidden = true;
      for (const key of ['width', 'length']) {
        const input = this.querySelector(`[data-fit-${key}]`);
        if (!input.validity.valid || !Number.isFinite(this.values[key]) || this.values[key] <= 0) {
          this.fail(this.measureError, `Please enter your tee’s ${key === 'width' ? 'chest width' : 'length'}, using a number greater than zero.`, input);
          return;
        }
      }
      const comparison = this.constructor.compare(this.garments, this.values.width, this.values.length);
      if (!comparison) {
        this.measureError.textContent = 'This product has no sizes supported by this guide.';
        this.measureError.hidden = false;
        return;
      }
      this.clearSelection();
      this.measured = true;
      this.candidates = comparison.sizes;
      this.comparison.textContent = `${comparison.outside ? 'Your tee is wider, narrower, longer, or shorter than the sizes in our chart. There may not be a close match. ' : ''}${comparison.sizes.join(' and ')} ${comparison.sizes.length === 1 ? 'is' : 'are'} closest to your tee’s chest width. Tap a size to see how much wider or narrower, and longer or shorter, it would be.`;
      this.comparison.hidden = false;
      this.renderRows();
      this.comparison.focus();
    }

    selectedOptions() {
      const picker = this.root?.querySelector('variant-selects');
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown')).map((group) => {
        const selected = group.querySelector('input:checked') || group.querySelector('select option:checked');
        return selected?.value || '';
      });
    }

    matchingVariant(size) {
      const index = Number(this.dataset.sizeOptionIndex);
      const selected = this.selectedOptions();
      if (index < 0 || !selected.length) return null;
      return this.variants.find((variant) => variant.options.every((option, position) =>
        position === index ? normalizeSize(option) === size : option === selected[position]));
    }

    stockMessage(size) {
      const index = Number(this.dataset.sizeOptionIndex);
      if (!this.variants.some((variant) => normalizeSize(variant.options[index]) === size)) return 'Not offered on this product.';
      const variant = this.matchingVariant(size);
      if (!variant) return 'This size does not match the current options. Close this window and check your color selection.';
      return variant.available ? '' : 'Unavailable in the selected color. We will not substitute a different size.';
    }

    renderRows() {
      this.querySelectorAll('[data-fit-row]').forEach((row) => {
        const candidate = this.candidates.includes(row.dataset.fitRow);
        row.classList.toggle('fit-finder__candidate', candidate);
        row.querySelector('[data-fit-row-note]').textContent = candidate ? (this.measured ? 'Similar width' : 'Compare') : '';
      });
    }

    difference(amount, positive, negative) {
      // Round only the displayed delta. Do not present a nonzero sub-display difference as 0 wider/longer.
      const value = Number(this.display(Math.abs(amount)));
      return value === 0 ? 'same measurement' : `${value} ${this.unit} ${amount > 0 ? positive : negative}`;
    }

    renderSelection() {
      const panel = this.querySelector('[data-fit-selection]');
      const garment = this.garments.find((item) => item.size === this.selectedSize);
      panel.hidden = !garment;
      if (!garment) return;
      const stock = this.stockMessage(garment.size);
      this.querySelector('[data-fit-selection-title]').textContent = `Size ${garment.size} · ${this.display(garment.width)} × ${this.display(garment.length)} ${this.unit}`;
      this.querySelector('[data-fit-selection-copy]').textContent = this.measured
        ? `Compared with your tee — chest: ${this.difference(garment.width - this.values.width, 'wider', 'narrower')}; back: ${this.difference(garment.length - this.values.length, 'longer', 'shorter')}. Think about whether you’d like that extra room or length. The shoulders and sleeves may fit differently too.`
        : 'These numbers show the tee’s width and length when laid flat. Compare both with a tee you love before choosing.';
      this.querySelector('[data-fit-selection-stock]').textContent = stock || 'Available in your chosen color.';
      const apply = this.querySelector('[data-fit-apply]');
      apply.disabled = Boolean(stock);
      apply.textContent = `Select size ${garment.size}`;
    }

    applySize() {
      if (!this.selectedSize) return;
      if (this.stockMessage(this.selectedSize)) { this.renderSelection(); return; }
      const picker = this.root?.querySelector('variant-selects');
      const size = this.selectedSize;
      const input = Array.from(picker.querySelectorAll('input[data-option-name]')).find((item) =>
        item.dataset.optionName.toLowerCase() === 'size' && normalizeSize(item.value) === size);
      const select = Array.from(picker.querySelectorAll('select')).find((item) =>
        Array.from(item.options).some((option) => option.dataset.optionName?.toLowerCase() === 'size'));
      const option = select && Array.from(select.options).find((item) => normalizeSize(item.value) === size);
      if (!input && !option) return;
      this.resetFinder();
      this.dialog.close();
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus({ preventScroll: true });
      } else {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        select.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('fit-finder', FitFinder);
})();
